import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Zap, Clock, DollarSign, Calendar, Shield, Tag } from "lucide-react";
import { VerifiedTraderRating } from "@/components/common/VerifiedTraderRating";
import { FIRM_BRANDING } from "@/utils/formatFirmDisplay";
import { AffiliateCTA } from "@/components/ui/AffiliateCTA";
import { getAffiliateLink } from "@/utils/tracking";
import { isPublicEvalType } from "@/utils/constants";
import { getSizeNumeric } from "@/utils/sizeUtils";
import { formatFirmDisplay } from "@/utils/formatFirmDisplay";

// PTP chip — tiny inline badge for card components
const PtpChip = ({ fd }) => {
  const ptp = fd.ptpEstimate;
  if (!ptp?.total) return null;
  const color = ptp.total <= 10 ? "text-emerald-400" : ptp.total <= 20 ? "text-amber-400" : "text-rose-400";
  const label = ptp.total <= 10 ? "Fast" : ptp.total <= 20 ? "Moderate" : "Slow";
  return (
    <div className="flex items-center gap-1.5 text-[11px]" title="Estimated time to first payout based on trading requirements, buffer rules, and payout processing." data-testid="ptp-chip">
      <Clock size={11} className={color} />
      <span className={`font-mono font-semibold ${color}`}>{fd.ptpDisplay}</span>
      <span className="text-slate-500">to payout</span>
      <span className={`${color} opacity-70 font-medium`}>· {label}</span>
    </div>
  );
};

// Get firm colors from FIRM_BRANDING (single source of truth)
const getfirmColors = (firm) => {
  const branding = FIRM_BRANDING[firm.slug];
  if (branding) {
    return { 
      bg: branding.brandColor, 
      text: "#ffffff", 
      accent: branding.brandColor 
    };
  }
  if (firm.brand_color) return { bg: firm.brand_color, text: "#ffffff", accent: firm.brand_color };
  return { bg: "#64748b", text: "#ffffff", accent: "#64748b" };
};

// Helper to check if firm has a specific drawdown type
const hasDrawdownType = (firm, type) => {
  const types = firm.drawdown_types || [firm.drawdown_type].filter(Boolean);
  return types.some(t => t?.toLowerCase().includes(type.toLowerCase()));
};

// Get firm badge based on characteristics
const getFirmBadge = (firm) => {
  if (firm.payout_frequency?.toLowerCase().includes("daily")) return { label: "Fastest Payout", icon: Zap };
  if (firm.min_days === 1) return { label: "1-Day Evaluation", icon: Clock };
  if (firm.profit_split?.includes("100%")) return { label: "100% First Profits", icon: DollarSign };
  if (hasDrawdownType(firm, "eod")) return { label: "EOD Drawdown", icon: Shield };
  if (firm.funded_min_days === 0 || firm.funded_min_days === "None") return { label: "No Min Funded Days", icon: Calendar };
  return { label: "1-Step Challenge", icon: Zap };
};

// Why traders choose this firm
const getWhyChoose = (firm) => {
  const reasons = [];
  
  if (firm.profit_split?.includes("100%")) {
    reasons.push("Keep 100% of first profits");
  } else if (firm.profit_split) {
    reasons.push(`${firm.profit_split} profit split`);
  }
  
  if (firm.payout_frequency?.toLowerCase().includes("daily")) {
    reasons.push("Daily payouts available");
  } else if (firm.payout_frequency?.toLowerCase().includes("weekly")) {
    reasons.push("Weekly payout schedule");
  }
  
  if (firm.min_days === 1) {
    reasons.push("Pass evaluation in 1 day");
  } else if (firm.min_days > 1 && firm.min_days <= 3) {
    reasons.push(`Only ${firm.min_days} minimum trading days`);
  } else if (firm.min_days === 0 || firm.min_days === null || firm.min_days === undefined) {
    reasons.push("No minimum trading days");
  }
  
  if (hasDrawdownType(firm, "eod")) {
    reasons.push("EOD drawdown (more forgiving)");
  }
  
  if (firm.news_trading) {
    reasons.push("News trading allowed");
  }
  
  if (firm.algos_allowed) {
    reasons.push("Algorithmic trading allowed");
  }
  
  return reasons.slice(0, 3);
};

// Data Grid Cell
const DataCell = ({ label, value, highlight = false }) => (
  <div className="py-3 px-4 bg-slate-800/30 rounded-lg">
    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
    <p className={`text-sm font-semibold ${highlight ? "text-emerald-400" : "text-white"}`}>
      {value || "—"}
    </p>
  </div>
);

// Full Card View
export const FirmCardFull = ({ firm, pricing, selectedSize, index = 0 }) => {
  const badge = getFirmBadge(firm);
  const BadgeIcon = badge.icon;
  const whyChoose = getWhyChoose(firm);
  const colors = getfirmColors(firm);
  const branding = FIRM_BRANDING[firm.slug];
  
  // Find price for selected size using numeric comparison
  const priceData = pricing?.find(p => getSizeNumeric(p) === selectedSize);
  const f = formatFirmDisplay(firm, priceData || pricing?.[0]);
  const displayPrice = f.pricePrimary;
  const displayBuffer = priceData?.buffer || pricing?.[0]?.buffer || "Varies";

  // Use brand color for text - check multiple slug formats
  const brandingKey = firm.slug?.toLowerCase().replace(/\s+/g, '-');
  const firmBranding = branding || FIRM_BRANDING[brandingKey] || FIRM_BRANDING[firm.slug];
  const nameColor = firmBranding?.textColor || firmBranding?.brandColor || colors.accent;
  const accentColor = firmBranding?.brandColor || colors.accent;
  const isLightText = nameColor === "#FFFFFF" || nameColor === "#ffffff" || nameColor?.toLowerCase() === "white";

  return (
    <article 
      className="bg-slate-900/40 border border-slate-800/60 rounded-xl overflow-hidden hover:border-slate-700/80 transition-all duration-200 relative flex flex-col"
      style={{ borderLeftWidth: "3px", borderLeftColor: accentColor }}
      data-testid={`firm-card-${firm.slug}`}
    >
      {/* Promo Badge - Floating Corner */}
      {f.discountValid && f.discountDisplay && (
        <div className="absolute top-0 right-0 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-bl-lg shadow-lg z-10" data-testid={`promo-badge-${firm.slug}`}>
          {f.discountDisplay}
        </div>
      )}

      {/* ===== FIRM IDENTITY HEADER ===== */}
      <header className="flex items-center justify-between p-5 border-b border-slate-800/40">
        <div className="flex items-center gap-4">
          {/* Logo - bigger size, fills container */}
          {firmBranding?.logo ? (
            <div className="firm-logo-tile w-16 h-16 rounded-xl shadow-lg p-1.5 bg-slate-800">
              <img 
                src={firmBranding.logo} 
                alt={firmBranding.logoAlt || `${firm.name} logo`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="w-full h-full items-center justify-center font-bold text-sm hidden"
                style={{ backgroundColor: accentColor, color: '#fff' }}
              >
                {firm.name?.substring(0, 3).toUpperCase()}
              </div>
            </div>
          ) : (
            <div 
              className="firm-logo-tile w-16 h-16 rounded-xl font-bold text-lg shadow-lg"
              style={{ backgroundColor: accentColor, color: '#fff' }}
            >
              {firm.icon || firm.name?.substring(0, 3).toUpperCase()}
            </div>
          )}
          
          {/* Name + Badge - Name color matches brand */}
          <div>
            <h3 
              className="text-lg font-bold flex items-center gap-2"
              style={{ color: nameColor, textShadow: isLightText ? '0 1px 4px rgba(0,0,0,0.7)' : 'none' }}
            >
              {firm.name}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span 
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium"
                style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
              >
                <BadgeIcon size={10} />
                {badge.label}
              </span>
            </div>
          </div>
        </div>
        <VerifiedTraderRating rating={firm.verified_trader_rating} count={firm.verified_trader_rating_count} tone="dark" />
      </header>

      {/* ===== DATA GRID ===== */}
      <div className="p-5 flex-1 flex flex-col">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
          <div className="bg-white/[0.03] rounded-lg p-2.5" data-testid={`price-cell-${firm.slug}`}>
            <p className="text-[9px] uppercase tracking-wider text-slate-600 mb-0.5">{f.priceLabel}</p>
            <p className="text-sm font-bold text-emerald-400 font-mono">{displayPrice}</p>
            {f.discountValid && f.priceOriginal && (
              <p className="text-[10px] text-slate-500 line-through font-mono">{f.priceOriginal}</p>
            )}
          </div>
          <DataCell label="Profit Split" value={f.profitSplit} />
          <DataCell label={f.drawdownLabel} value={f.drawdownShort} />
          <DataCell label="Min Eval Days" value={f.minEvalDays} />
          <DataCell label="Payout Schedule" value={f.payoutSchedule} />
          {f.activationFee ? (
            <DataCell label="Activation Fee" value={f.activationFeeForSize || f.activationFeeRaw || f.activationFee} />
          ) : (
            <DataCell label="Min Hold Time" value={f.minHoldTime} highlight={firm.min_hold_seconds === 0} />
          )}
        </div>

        {/* ===== WHY TRADERS CHOOSE ===== */}
        {whyChoose.length > 0 && (
          <div className="mb-5 pt-4 border-t border-slate-800/40">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Why traders choose this firm</p>
            <ul className="space-y-1">
              {whyChoose.map((reason, i) => (
                <li key={i} className="text-xs text-slate-400 flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  {reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ===== CTA ZONE ===== */}
        <div className="flex flex-col gap-2 pt-4 border-t border-slate-800/40 mt-auto">
          <PtpChip fd={f} />
          {/* Promo Code Display */}
          {f.discountValid && f.couponCode && (
            <div className="flex items-center justify-center gap-2 text-[10px] sm:text-xs text-emerald-400 bg-emerald-500/10 py-1.5 px-2 rounded-lg leading-snug">
              <Tag size={12} />
              <span>Use code <span className="font-mono font-bold text-white">{f.couponCode}</span> for {f.discountDisplay}</span>
            </div>
          )}
          <div className="flex items-center gap-3">
            <AffiliateCTA
              href={getAffiliateLink(firm)}
              label="Get Funded"
              firmSlug={firm.slug}
              placement="firm_card"
              size="md"
              className="flex-1"
              data-testid={`cta-get-funded-${firm.slug}`}
            />
            <Link
              to={`/firms/${firm.slug}`}
              className="px-4 py-2.5 text-slate-400 hover:text-white text-sm font-medium transition-colors"
              data-testid={`cta-details-${firm.slug}`}
            >
              Details
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};

// Mobile Compact Card (stacked card for small screens)
const FirmCardMobile = ({ firm, pricing, selectedSize, index = 0 }) => {
  const priceData = pricing?.find(p => getSizeNumeric(p) === selectedSize);
  const f = formatFirmDisplay(firm, priceData || pricing?.[0]);
  const displayPrice = f.pricePrimary;
  const colors = getfirmColors(firm);
  const branding = FIRM_BRANDING[firm.slug];

  return (
    <div
      className="bg-slate-900/30 border border-slate-800/40 rounded-xl p-4 transition-colors"
      style={{ borderLeftWidth: "3px", borderLeftColor: colors.accent }}
      data-testid={`firm-mobile-${firm.slug}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {branding?.logo ? (
            <div className="w-10 h-10 rounded-lg shadow-md flex items-center justify-center p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/50">
              <img src={branding.logo} alt={firm.name} className="w-full h-full object-contain" onError={e => { e.target.style.display = "none"; }} />
            </div>
          ) : (
            <div
              className="w-10 h-10 rounded-lg font-bold text-xs shadow-md flex items-center justify-center"
              style={{ backgroundColor: colors.bg, color: colors.text }}
            >
              {firm.icon || firm.name?.substring(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <p className="text-white font-semibold text-sm">{firm.name}</p>
            <VerifiedTraderRating rating={firm.verified_trader_rating} tone="dark" showLabel={false} size="xs" />
          </div>
        </div>
        <span className="text-lg font-bold text-emerald-400">{displayPrice}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <p className="text-[9px] uppercase text-slate-600 mb-0.5">{f.drawdownLabel}</p>
          <p className="text-xs font-medium text-white">{f.drawdownShort}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-slate-600 mb-0.5">Payout</p>
          <p className="text-xs font-medium text-white">{f.payoutShort}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-slate-600 mb-0.5">Min Days</p>
          <p className="text-xs font-medium text-white">{f.minEvalDaysShort}</p>
        </div>
      </div>
      <PtpChip fd={f} />
      <Link
        to={`/firms/${firm.slug}`}
        className="flex items-center justify-center gap-1.5 w-full py-2.5 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 rounded-lg text-xs font-medium text-white transition-colors"
        data-testid={`firm-mobile-details-${firm.slug}`}
      >
        View Details <ChevronRight size={14} />
      </Link>
    </div>
  );
};

// Compact Row View
export const FirmCardCompact = ({ firm, pricing, selectedSize, index = 0 }) => {
  const priceData = pricing?.find(p => getSizeNumeric(p) === selectedSize);
  const f = formatFirmDisplay(firm, priceData || pricing?.[0]);
  const displayPrice = f.pricePrimary;
  const colors = getfirmColors(firm);
  const branding = FIRM_BRANDING[firm.slug];
  
  return (
    <div 
      className="flex items-center gap-4 py-4 px-5 bg-slate-900/30 hover:bg-slate-900/50 border-b border-slate-800/30 transition-colors"
      style={{ borderLeftWidth: "3px", borderLeftColor: colors.accent }}
      data-testid={`firm-row-${firm.slug}`}
    >
      {/* Firm Identity */}
      <div className="flex items-center gap-3 w-56 flex-shrink-0">
        {branding?.logo ? (
          <div className="w-9 h-9 rounded-lg shadow-md flex items-center justify-center p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/50 flex-shrink-0">
            <img src={branding.logo} alt={firm.name} className="w-full h-full object-contain" onError={e => { e.target.style.display = "none"; }} />
          </div>
        ) : (
          <div 
            className="w-9 h-9 rounded-lg font-bold text-xs shadow-md flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {firm.icon || firm.name?.substring(0, 2).toUpperCase()}
          </div>
        )}
        <div>
          <p className="text-white font-medium text-sm">{firm.name}</p>
          <VerifiedTraderRating rating={firm.verified_trader_rating} tone="dark" showLabel={false} size="xs" />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="flex-1 grid grid-cols-5 gap-8">
        <div>
          <p className="text-[9px] uppercase text-slate-600">{f.priceLabel}</p>
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-emerald-400">{displayPrice}</p>
            {f.discountValid && f.discountDisplay && (
              <span className="text-[8px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">{f.discountDisplay}</span>
            )}
          </div>
        </div>
        <div>
          <p className="text-[9px] uppercase text-slate-600">{f.drawdownLabel}</p>
          <p className="text-sm font-medium text-white">{f.drawdownShort}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-slate-600">Payout</p>
          <p className="text-sm font-medium text-white">{f.payoutShort}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-slate-600">Min Days</p>
          <p className="text-sm font-medium text-white">{f.minEvalDaysShort}</p>
        </div>
        <div>
          <p className="text-[9px] uppercase text-slate-600">Path to Payout</p>
          <p className={`text-sm font-mono font-semibold ${f.ptpEstimate?.total <= 10 ? "text-emerald-400" : f.ptpEstimate?.total <= 20 ? "text-amber-400" : "text-rose-400"}`}>{f.ptpDisplay || "—"}</p>
        </div>
      </div>

      {/* CTA */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <AffiliateCTA
          href={getAffiliateLink(firm)}
          label="Get Funded"
          firmSlug={firm.slug}
          placement="firm_card"
          size="md"
          data-testid={`cta-get-funded-row-${firm.slug}`}
        />
        <Link
          to={`/firms/${firm.slug}`}
          className="p-2 text-slate-500 hover:text-white transition-colors"
        >
          <ChevronRight size={16} />
        </Link>
      </div>
    </div>
  );
};

// View Toggle Component
export const ViewToggle = ({ view, setView }) => (
  <div className="flex items-center gap-1 bg-slate-800/50 rounded-lg p-1">
    <button
      onClick={() => setView("full")}
      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        view === "full" 
          ? "bg-slate-700 text-white" 
          : "text-slate-500 hover:text-slate-300"
      }`}
    >
      Full View
    </button>
    <button
      onClick={() => setView("compact")}
      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        view === "compact" 
          ? "bg-slate-700 text-white" 
          : "text-slate-500 hover:text-slate-300"
      }`}
    >
      Compact
    </button>
  </div>
);

// Main Firm List Component
export const FirmList = ({ firms, selectedSize }) => {
  const [view, setView] = useState("full");
  
  // When no size is selected, show ALL firms with all pricing
  // When a size is selected, show all firms that have pricing for that size (standard eval types only)
  const firmsWithPricing = firms.map(firm => {
    const pricing = (firm.pricing || []).filter(p => isPublicEvalType(p.evaluation_type));
    return { firm, pricing };
  }).filter(({ pricing }) => pricing.length > 0);

  const firmCount = firmsWithPricing.length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-slate-500 text-sm">
            {selectedSize
              ? `${firmCount} futures prop firms for $${selectedSize.toLocaleString()}`
              : `${firmCount} futures prop firms`}
          </p>
        </div>
        <div className="w-full sm:w-auto">
          <ViewToggle view={view} setView={setView} />
        </div>
      </div>

      {/* Firm Cards */}
      {view === "full" ? (
        <div className="grid lg:grid-cols-2 gap-4">
          {firmsWithPricing.map(({ firm, pricing }, idx) => (
            <FirmCardFull 
              key={firm.id} 
              firm={firm} 
              pricing={pricing}
              selectedSize={selectedSize}
              index={idx}
            />
          ))}
        </div>
      ) : (
        <>
          {/* Desktop: table rows */}
          <div className="hidden md:block bg-slate-900/20 rounded-xl border border-slate-800/40 overflow-hidden">
            {/* Compact Header */}
            <div className="flex items-center gap-4 py-3 px-5 border-b border-slate-800/50 bg-slate-900/40">
              <div className="w-56 flex-shrink-0">
                <span className="text-[9px] uppercase tracking-wider text-slate-600">Firm</span>
              </div>
              <div className="flex-1 grid grid-cols-5 gap-8">
                <span className="text-[9px] uppercase tracking-wider text-slate-600">Cost</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-600">Drawdown</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-600">Payout</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-600">Min Days</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-600">Path to Payout</span>
              </div>
              <div className="w-32 flex-shrink-0"></div>
            </div>
            {firmsWithPricing.map(({ firm, pricing }, idx) => (
              <FirmCardCompact 
                key={firm.id} 
                firm={firm} 
                pricing={pricing}
                selectedSize={selectedSize}
                index={idx}
              />
            ))}
          </div>
          {/* Mobile: stacked cards */}
          <div className="md:hidden space-y-3">
            {firmsWithPricing.map(({ firm, pricing }, idx) => (
              <FirmCardMobile
                key={firm.id}
                firm={firm}
                pricing={pricing}
                selectedSize={selectedSize}
                index={idx}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
